/**
 * Compute Risk Assessment metrics from a pick's action string.
 *
 * The LLM emits free-form `action` text per pick (parsed by parseAction).
 * We derive R:R, Risk per share %, dollar risk per share for the Risk
 * Assessment table and modal. Returns null when entry/stop/target can't
 * be extracted — UI shows "—" in that case.
 *
 * Target precedence: LLM's takeProfitPrice > frontend-derived T1 (+1%
 * fallback). T1 is a manual scale-out reference, not a real LLM target,
 * so prefer the LLM value when available.
 */

import { parseAction, extractPrice } from './actionParser';
import { RISK_AGREEMENT_TOLERANCE, SECTOR_ABBREV } from '../constants/riskMetrics';

/**
 * Return {entry, stop, target, riskPct, rr, dollarRiskPerShare} or null.
 */
export function computeMetrics(action) {
  const parsed = parseAction(action);
  if (!parsed) return null;

  const entry = parsed.entryPrice;
  const stop = extractPrice(parsed.stopLoss);
  const target = parsed.takeProfitPrice || parsed.targets?.t1?.price;

  if (!entry || !stop || !target) return null;
  // Stop above entry would be a short trade or parse error — bail.
  if (stop >= entry) return null;

  return {
    entry,
    stop,
    target,
    riskPct: ((entry - stop) / entry) * 100,
    rr: (target - entry) / (entry - stop),
    dollarRiskPerShare: entry - stop,
    // Flag whether target came from LLM or T1 fallback so UI can show *
    targetFromLlm: parsed.takeProfitPrice != null,
  };
}

/** Position $ ÷ entry, rounded down to whole shares. */
export function computeShares(positionDollars, entry) {
  if (!positionDollars || !entry || entry <= 0) return null;
  return Math.floor(positionDollars / entry);
}

/** Shares × $ risk per share = max dollar loss if stopped. */
export function computeMaxLoss(positionDollars, metrics) {
  const shares = computeShares(positionDollars, metrics?.entry);
  if (shares == null || !metrics) return null;
  return shares * metrics.dollarRiskPerShare;
}

/** (price - vwap) / vwap × 100. Negative = price below VWAP. */
export function computeVwapDistance(price, vwap) {
  if (!price || !vwap) return null;
  return ((price - vwap) / vwap) * 100;
}

/**
 * Decide whether two AI plans agree (within tolerance) or differ.
 * Returns {agreement: bool, claudeMetrics, gptMetrics}.
 */
export function comparePlans(claudeAction, gptAction) {
  const c = computeMetrics(claudeAction);
  const g = computeMetrics(gptAction);
  if (!c || !g) return { agreement: false, claudeMetrics: c, gptMetrics: g };

  const riskDelta = Math.abs(c.riskPct - g.riskPct);
  const rrDelta = Math.abs(c.rr - g.rr);
  const agreement =
    riskDelta < RISK_AGREEMENT_TOLERANCE.riskPctDelta &&
    rrDelta < RISK_AGREEMENT_TOLERANCE.rrDelta;
  return { agreement, claudeMetrics: c, gptMetrics: g };
}

/**
 * Sum max-loss across selectedSymbols. For symbols with two AI plans
 * (Claude + GPT), conservatively use whichever plan has the larger
 * per-share risk (worst-case max loss).
 *
 * @param selectedSymbols  Set<string> of symbols included in plan
 * @param claudeActions    Map<symbol, action-string>
 * @param gptActions       Map<symbol, action-string>
 * @param positionByStock  Map<symbol, positionDollars>
 * @param defaultPosition  fallback when symbol absent in positionByStock
 */
export function computeTotalRisk(
  selectedSymbols,
  claudeActions,
  gptActions,
  positionByStock,
  defaultPosition
) {
  let total = 0;
  for (const symbol of selectedSymbols) {
    const c = computeMetrics(claudeActions?.[symbol]);
    const g = computeMetrics(gptActions?.[symbol]);
    let ref = null;
    if (c && g) {
      ref = c.dollarRiskPerShare > g.dollarRiskPerShare ? c : g;
    } else {
      ref = c || g;
    }
    if (!ref) continue;
    const pos = positionByStock[symbol] ?? defaultPosition;
    const loss = computeMaxLoss(pos, ref);
    if (loss != null) total += loss;
  }
  return total;
}

/** Polygon's SIC sector → 6-8 char abbreviation for narrow table column. */
export function abbreviateSector(sector) {
  if (!sector) return '—';
  return SECTOR_ABBREV[sector] || sector.slice(0, 7);
}

/**
 * Format the metadata line (R:R · Risk % · Position → Max loss) for a
 * stockRisk card. Handles single-plan, agreement (averaged), and
 * conflict (dual Claude/GPT) cases. Returns {display, conflict}.
 */
export function formatMetadataLine(claudeAction, gptAction, positionDollars) {
  const { agreement, claudeMetrics: c, gptMetrics: g } = comparePlans(
    claudeAction,
    gptAction
  );

  if (!c && !g) return null;

  if (!c || !g) {
    // Only one AI provided a plan.
    const m = c || g;
    const loss = computeMaxLoss(positionDollars, m);
    const shares = computeShares(positionDollars, m.entry);
    return {
      display:
        `R:R ${m.rr.toFixed(2)}:1 · Risk ${m.riskPct.toFixed(1)}% · ` +
        `${formatDollars(positionDollars)} → ${sharesLabel(shares)} → Max $${formatLoss(loss)}`,
      conflict: false,
    };
  }

  if (agreement) {
    const avgRR = (c.rr + g.rr) / 2;
    const avgRisk = (c.riskPct + g.riskPct) / 2;
    const cLoss = computeMaxLoss(positionDollars, c);
    const gLoss = computeMaxLoss(positionDollars, g);
    const avgLoss = ((cLoss ?? 0) + (gLoss ?? 0)) / 2;
    const shares = computeShares(positionDollars, (c.entry + g.entry) / 2);
    return {
      display:
        `R:R ${avgRR.toFixed(2)}:1 · Risk ${avgRisk.toFixed(1)}% · ` +
        `${formatDollars(positionDollars)} → ${sharesLabel(shares)} → Max $${formatLoss(avgLoss)}`,
      conflict: false,
    };
  }

  // Conflict — show both AIs' values
  const cLoss = computeMaxLoss(positionDollars, c);
  const gLoss = computeMaxLoss(positionDollars, g);
  const cShares = computeShares(positionDollars, c.entry);
  const gShares = computeShares(positionDollars, g.entry);
  return {
    display:
      `R:R ${c.rr.toFixed(2)}/${g.rr.toFixed(2)}:1 · ` +
      `Risk ${c.riskPct.toFixed(1)}%/${g.riskPct.toFixed(1)}% · ` +
      `${formatDollars(positionDollars)} → ${cShares}/${gShares} shares → ` +
      `Max $${formatLoss(cLoss)}/$${formatLoss(gLoss)}`,
    conflict: true,
  };
}

function formatDollars(n) {
  if (n == null) return '?';
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${n}`;
}

// Share count with a grammatical unit: 1 share, 120 shares.
const sharesLabel = (n) => `${n} ${n === 1 ? 'share' : 'shares'}`;

function formatLoss(n) {
  if (n == null) return '?';
  return Math.round(n).toLocaleString('en-US');
}
